<template>
  <AuthPage
    context="login"
    data-id="auth-page-login"
    title="Login"
    :form-button="usePassword ? 'Login' : 'Send access link'"
    :done="done"
    :loading="loading"
    :show-federated-login="true"
    :show-form="showForm"
    @submit="submit"
  >
    <template #done>
      <DoneImage
        done-message="Open your email and click the verification link."
        :done="true"
        done-image="/open-email.svg"
      />
    </template>
    <template #notes>
      <AuthNotesCard>
        Please check your spam folder and allow emails from
        <strong>getmailslurp.com</strong>
      </AuthNotesCard>
    </template>
    <template #subtitle>
      Don't have an account?
      <AuthLink
        title="Register for free"
        to="/sign-up"
        data-id="auth-link-signup"
      />
    </template>
    <template #form>
      <AuthFormInput
        id="email"
        v-model="username"
        label="Email address"
        type="email"
        placeholder="you@company.com"
        required
        autocomplete="username"
        data-id="email"
      >
        <template #label>
          <AuthLabelLink
            :text="!usePassword ? 'Use password' : 'Use email link'"
            tabindex="-1"
            :data-enabled="usePassword ? '1' : '0'"
            data-id="password-toggle-button"
            @click="usePassword = !usePassword"
          />
        </template>
      </AuthFormInput>
      <AuthFormInput
        id="password"
        v-if="usePassword"
        v-model="password"
        label="Password"
        type="password"
        autocomplete="current-password"
        required
        data-id="password"
      >
        <template #label>
          <AuthLabelLink
            data-id="forgot-password-button"
            text="Forgot password?"
            @click="gotoForgot"
          />
        </template>
      </AuthFormInput>
    </template>
    <template #links>
      <SAMLSSOLink />
      <NuxtLink class="text-link" to="/organization"
        >Organization login</NuxtLink
      >
    </template>
  </AuthPage>
</template>
<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useStore3 } from '../../../store/main';
import { ErrorCodesDtoAppErrorCodesEnum } from '@mailslurp/admin-sdk';
import type { ApiError } from '../../../lib/services/helpers';
import AuthPage from '../AuthPage.vue';
import AuthLink from '../AuthLink.vue';
import AuthFormInput from '../AuthFormInput.vue';
import DoneImage from '../../card/DoneImage.vue';
import { DEFAULT_SIGNUP_WITH_PASSWORD } from '../../../lib/services/configFlags';
import type { ErrorPayload } from '../../../store/types';
import AuthNotesCard from '../../../components/auth/AuthNotesCard.vue';
import AuthLabelLink from './AuthLabelLink.vue';
import SAMLSSOLink from '../../../components/auth/SAMLSSOLink.vue';
import { useRoute, navigateTo } from '#imports';
import { useNuxtApp } from '#imports';
import { getLogger } from '../../../lib/services/getLogger';
const { $captureEvent: captureEvent } = useNuxtApp();
const store3 = useStore3();
const route = useRoute();

const log = getLogger('login.vue');
const username = ref('');
const password = ref('');
const usePassword = ref(DEFAULT_SIGNUP_WITH_PASSWORD);
const loading = ref(false);
const done = ref<boolean>(false);
const preventMultiple = ref(true);
const showForm = ref(false);
const gotoForgot = async () => await navigateTo('/forgot-password');

onMounted(async () => {
  // check for returning saml user
  const {
    token = null,
    samlRedirect = null,
    email = null,
    password: _password = null,
    noFlash = null,
    submit: _submit = null,
    allowMultiple = null,
    allowPassword = null,
    useEmail = null,
  } = route.query ?? {};
  log('login params: ' + JSON.stringify(route.query));
  if (noFlash) {
    store3.setFlash(null);
  }
  if (email) {
    username.value = email as string;
    showForm.value = true;
  }
  if (allowMultiple) {
    preventMultiple.value = false;
  }
  if (_password) {
    password.value = _password as string;
    showForm.value = true;
  }
  if (allowPassword) {
    usePassword.value = true;
    showForm.value = true;
  }
  if (useEmail) {
    usePassword.value = false;
    showForm.value = true;
  }
  if (token) {
    loading.value = true;
    try {
      log('Token login, set api key cache');
      const user = await store3.getUserForToken(token as string);
      log(`Returned user ${user?.id}`);
      await store3.setUserApiKey(user);
      store3.setIsSamlRedirect(samlRedirect as string);
      await navigateTo('/dashboard/');
    } catch (e) {
      loading.value = false;
    }
  }
  if (_submit) {
    await submit();
  }
});

const submit = async () => {
  store3.setFlash(null);
  loading.value = true;
  try {
    if (usePassword.value) {
      log('Logging in with password user ' + username.value);
      const loginPayload = {
        emailAddress: username.value,
        password: password.value,
        captchaToken: undefined,
      };
      captureEvent('begin_login', {
        method: 'password',
      });
      const _user = await store3.loginPassword(loginPayload);
      await store3.setUserApiKey(_user);
      if (_user && _user.apiKey) {
        await navigateTo('/dashboard');
      }
      loading.value = false;
    } else {
      log('Logging in with username ' + username.value);
      const signInPayload = {
        emailAddress: username.value,
        preventMultipleUsers: preventMultiple.value,
      };
      captureEvent('begin_login', {
        method: 'email',
      });
      await store3.loginLink(signInPayload);
      loading.value = false;
      done.value = true;
    }
  } catch (e: unknown) {
    // TODO show unblock page if user cannot use magic link
    loading.value = false;
    const er: ErrorPayload = {
      error: e,
      text: `Error logging in. Please try again. ${e}`,
    };
    captureEvent('begin_login_error', {
      method: usePassword.value ? 'password' : 'email',
    });
    const apiError = e as ApiError;
    if (
      apiError?.apiResult?.errorCode ===
      ErrorCodesDtoAppErrorCodesEnum.W_401_MULTIPLE_ACCOUNTS
    ) {
      await navigateTo(
        '/accounts?emailAddress=' + username.value + '&isLogin=true',
      );
    }
    store3.setError(er);
  }
};
</script>
